import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { isGoogleMapsReady, loadGoogleMapsScript } from 'src/utils/CommonFunctions';

/**
 * Loads the Google Maps JS API on demand, and ONLY for the components that actually use it.
 *
 * The script used to be injected for every signed-in user at app boot (App.tsx), so Home,
 * My Orders, Document Hub and Organization all paid for a Maps download they never used. It is
 * now requested by the four components that touch `window.google` — AutoCompleteGoogle, Map,
 * ShipperConsigneeAutocomplete and MapBox — so any page that renders one of them gets the script
 * automatically, and any page that does not never loads it.
 *
 * `isReady` flips to true once `google.maps.places` is usable. Consumers must keep it in their
 * effect dependencies: at first mount the script is typically NOT loaded yet, so an effect that
 * only runs once (deps `[]`) would find no `window.google` and silently never initialise.
 */
export const useGoogleMaps = () => {
    const { googleKey } = useSelector((state: RootState) => state.basic);
    const [isReady, setIsReady] = useState<boolean>(() => isGoogleMapsReady());

    useEffect(() => {
        if (isReady) return;

        // Redux holds the key once the map service resolves; window is the build-time fallback.
        const apiKey = googleKey || window.GOOGLE_API_KEY;

        if (!apiKey) return;

        let isCancelled = false;

        loadGoogleMapsScript(apiKey)
            .then(() => {
                if (!isCancelled) setIsReady(true);
            })
            .catch((error) => {
                console.error('Google Maps script failed to load:', error);
            });

        return () => {
            isCancelled = true;
        };
    }, [googleKey, isReady]);

    return { isReady };
};

export default useGoogleMaps;
